import { isEmpty } from 'lodash';
import { GetStaticPaths, GetStaticProps } from 'next';
import { useRouter } from 'next/router';
import { ReactElement } from 'react';

import { HeaderBlock } from '@/blocks/header-block';
import { LinkBankBlock } from '@/blocks/link-bank-block';
import { PageLayout } from '@/components/layouts/page-layout';
import { PageLoading } from '@/components/page-loading';
import { PAGE_REVALIDATE_INTERVAL_MONTH, RESPONSIVE_IMAGE_WORLD_MAP_DARK } from '@/lib/constants';
import { CountryPageInterface } from '@/types';
import { cmsPostService } from '@/utils/api/services/cms.service/post';
import { createMetaTagsWithCustomTitleAndDescription, createRoutePageUrl } from '@/utils/helpers';
import { findPlaceById } from '@/utils/places';
import { getGenericPlacePairs } from '@/utils/seo/seo-links';

type PageProps = { page: CountryPageInterface };

const Page = ({ page }: PageProps): ReactElement => {
  const router = useRouter();

  // If the page is not yet generated, this will be displayed
  // initially until getStaticProps() finishes running
  if (router.isFallback) {
    return <PageLoading />;
  }

  // Destructure page object
  const { country, seoMetaTags, canonical, fromLinks, toLinks, posts } = page;

  const content = {
    title: `Shipping to ${country.name}`,
    description: `See more about shipping freight to and from ${country.name}. Compare freight rates, transit times, route options, carriers and much more.`,
    image: RESPONSIVE_IMAGE_WORLD_MAP_DARK,
  };

  return (
    <PageLayout metaTags={seoMetaTags} canonical={canonical} headerVariant="light" posts={posts}>
      <div className="blocks">
        <HeaderBlock
          id="page-header"
          heading={content.title}
          body={`<p>${content.description}</p>`}
          variant="dark"
          image={content.image}
        />
        {!isEmpty(fromLinks) && (
          <LinkBankBlock id="from-links" heading={`Shipping from ${country.name}`} links={{ links: fromLinks }} />
        )}
        {!isEmpty(toLinks) && (
          <LinkBankBlock
            id="to-links"
            heading={`Shipping to ${country.name}`}
            links={{ links: toLinks }}
            noTopPadding
          />
        )}
      </div>
    </PageLayout>
  );
};

export const getStaticPaths: GetStaticPaths = async () => ({
  paths: [],
  fallback: true,
});

export const getStaticProps: GetStaticProps = async ({ params = {} }) => {
  const { origin } = params;
  // Query country from url
  const [country, posts] = await Promise.all([
    findPlaceById(origin?.toString() || ''),
    cmsPostService.getPostSnippetsCached(),
  ]);

  if (!country || !country.placeId) {
    return {
      notFound: true,
    };
  }

  // If match found, build SEO props
  const canonical = createRoutePageUrl(country.placeId, null, true);

  // Create SEO tags
  const seoMetaTags = createMetaTagsWithCustomTitleAndDescription({
    title: `Shipping cargo to and from ${country.name}`,
    description: `Compare freight rates, transit times and transport modes to find the best way to ship your freight and cargo to and from ${country.name}`,
  });

  // SEO links
  const fromLinks = getGenericPlacePairs(country, country.countryCode!, 'outbound');
  const toLinks = getGenericPlacePairs(country, country.countryCode!, 'inbound');

  return {
    props: {
      page: {
        country,
        canonical,
        seoMetaTags,
        fromLinks,
        toLinks,
        posts,
      },
    },
    revalidate: PAGE_REVALIDATE_INTERVAL_MONTH,
  };
};

export default Page;
